import { useEffect, useState } from "react";
import { fetchCustomerById } from "../../../services/customerService";
import { getFreshIdToken } from "../../../auth/getFreshIdToken";
import { debug } from "../../../utils/debugLogger";

export type PaymentState = "past_due" | "paused" | null;

/**
 * The selected Brand's billing state, for the banner and the paywall (SGL-283).
 *
 * Refetched on a slow interval as well as on Brand change, because the state
 * that matters most changes while the customer is sitting in the app: they go
 * and update their card in the Stripe portal, come back, and the paywall
 * should be gone. Waiting for a reload to notice would make a fixed problem
 * look unfixed.
 *
 * Failures return null — healthy. An outage on this endpoint must never put a
 * paywall in front of a paying customer, which is the one failure mode here
 * that would be genuinely unforgivable.
 */
const POLL_INTERVAL_MS = 2 * 60 * 1000;

export function usePaymentState(customerId: string | null): {
  paymentState: PaymentState;
  refetch: () => void;
} {
  const [paymentState, setPaymentState] = useState<PaymentState>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!customerId) {
      setPaymentState(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const idToken = await getFreshIdToken();
        if (!idToken) return;
        const customer = await fetchCustomerById(customerId, idToken);
        if (cancelled) return;
        const state = customer?.payment_state;
        setPaymentState(state === "past_due" || state === "paused" ? state : null);
      } catch (err) {
        debug.error("Could not read payment state:", err);
        if (!cancelled) setPaymentState(null);
      }
    };

    load();
    const id = window.setInterval(() => {
      // Nobody is looking at a hidden tab, and a paused Brand cannot become
      // unpaused without the customer acting. Polling it burns the API for
      // nothing.
      if (document.visibilityState === "visible") load();
    }, POLL_INTERVAL_MS);

    // The moment that matters most: they paid in the Stripe portal in another
    // tab and came back. Re-read immediately rather than up to two minutes
    // later, so a fixed problem never keeps looking broken.
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [customerId, tick]);

  return { paymentState, refetch: () => setTick((t) => t + 1) };
}
